import React, { useState } from 'react';
import moment from 'moment';

import { ModalConfirmationWithErrorAndLoader } from '../../common/ModalConfirmationWithErrorAndLoader';
import { formatWeekRangeLabel } from './utils';
import { TRebooksExtendingType, REBOOKS_EXTENDING_TYPE } from './types';

type TRebooksExtendConfirmationModalProps = {
    extendType: TRebooksExtendingType;
    selectedBookingCount: number;
    bookingsAfter: string | null;
    bookingsBefore: string | null;
    isExtending: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

export const RebooksExtendConfirmationModal = ({
    extendType,
    selectedBookingCount,
    bookingsAfter,
    bookingsBefore,
    isExtending,
    onCancel,
    onConfirm,
}: TRebooksExtendConfirmationModalProps) => {
    const [isInProgress, setIsInProgress] = useState(false);

    const sourceWeekLabel = bookingsAfter
        ? formatWeekRangeLabel(bookingsAfter, bookingsBefore)
        : null;

    const targetWeekLabel = (() => {
        const targetMonday = moment().add(1, 'week').startOf('isoWeek');
        const targetSunday = targetMonday.clone().endOf('isoWeek');
        return formatWeekRangeLabel(
            targetMonday.format('YYYY-MM-DD'),
            targetSunday.format('YYYY-MM-DD'),
        );
    })();

    const message =
        extendType === REBOOKS_EXTENDING_TYPE.SELECTED ? (
            <>
                Are you sure you want to extend{' '}
                <strong>
                    {selectedBookingCount} booking
                    {selectedBookingCount === 1 ? '' : 's'}
                </strong>
                {targetWeekLabel ? (
                    <> to the week of <strong>{targetWeekLabel}</strong></>
                ) : null}
                ?
            </>
        ) : (
            <>
                Are you sure you want to extend{' '}
                <strong>all bookings</strong>
                {sourceWeekLabel ? (
                    <> from week <strong>{sourceWeekLabel}</strong></>
                ) : null}
                {targetWeekLabel ? (
                    <> to the week of <strong>{targetWeekLabel}</strong></>
                ) : null}
                ?
            </>
        );

    return (
        <ModalConfirmationWithErrorAndLoader
            className="rebooks-extend-confirm-modal"
            errorMessage=""
            handleCancel={onCancel}
            handleContinue={onConfirm}
            isError={false}
            isInProgress={isExtending || isInProgress}
            setIsInProgress={setIsInProgress}
            isOpen={extendType !== null}
            title="Extend rebooks"
            continueButtonText="EXTEND"
        >
            <p className="modal-title">Extend rebooks</p>
            <p>{message}</p>
            <p className="margin-top-10">
                Once your bookings are created, we&apos;ll send you a summary
                email. The Next Week Availability column may take a moment to
                update — it refreshes automatically once everything has
                finished processing.
            </p>
        </ModalConfirmationWithErrorAndLoader>
    );
};
